import { useState } from "react";

import ErrorBoundary from "@/app/shared/ErrorBoundary";
import Valkyrie, { viChevronDown } from "@sippy-platform/valkyrie";

import { Card, CardContent, Collapse, Grid, IconButton, Stack } from "@mui/material";
import { cn } from "cn";
import PropTypes from "prop-types";

import FormTitle from "./FormTitle";

function FormGroup({ title, children, actions, collapsible, id, className, sx, tailwindApi, ...props }) {
  const [collapsed, setCollapsed] = useState(collapsible);

  if (tailwindApi) {
    return (
      <div
        className={cn(
          "@container/group rounded-lg bg-white p-4 shadow-[0.1px_0.2px_0.4px_rgba(0,0,0,0.028),0.2px_0.7px_1.3px_rgba(0,0,0,0.042),1px_3px_6px_rgba(0,0,0,0.07)] dark:bg-[#1f1f24]",
          className,
        )}
        id={id}
      >
        {(title || actions) && (
          <FormTitle
            title={title}
            actions={
              <Stack
                direction="row"
                sx={{
                  gap: 0.5,
                }}
              >
                {collapsible !== undefined ? (
                  <>
                    {actions}
                    <IconButton onClick={() => setCollapsed((prev) => !prev)}>
                      <Valkyrie icon={viChevronDown} rotate={!collapsed ? 180 : 0} />
                    </IconButton>
                  </>
                ) : (
                  actions
                )}
              </Stack>
            }
          />
        )}
        {collapsed === undefined ? (
          <div
            className={cn("grid grid-cols-[repeat(auto-fill,minmax(min(19.5rem,100%),1fr))] gap-4", {
              "mt-4": !!children,
            })}
            {...props}
          >
            {children}
          </div>
        ) : (
          <Collapse in={!collapsed}>
            <div
              className={cn("grid grid-cols-[repeat(auto-fill,minmax(min(19.5rem,100%),1fr))] gap-4", {
                "mt-4": !!children,
              })}
              {...props}
            >
              {children}
            </div>
          </Collapse>
        )}
      </div>
    );
  }

  return (
    <Card elevation={1} id={id}>
      <CardContent sx={sx}>
        {(title || actions) && (
          <FormTitle
            title={title}
            actions={
              <Stack
                direction="row"
                sx={{
                  gap: 0.5,
                }}
              >
                {collapsible !== undefined ? (
                  <>
                    {actions}
                    <IconButton onClick={() => setCollapsed((prev) => !prev)}>
                      <Valkyrie icon={viChevronDown} rotate={!collapsed ? 180 : 0} />
                    </IconButton>
                  </>
                ) : (
                  actions
                )}
              </Stack>
            }
          />
        )}
        <Collapse in={collapsed === undefined ? true : !collapsed}>
          <Grid container spacing={2} sx={{ pt: children ? 2 : 0 }} {...props}>
            <ErrorBoundary title={title}>{children}</ErrorBoundary>
          </Grid>
        </Collapse>
      </CardContent>
    </Card>
  );
}

FormGroup.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  children: PropTypes.node,
  actions: PropTypes.node,
  collapsible: PropTypes.bool,
  sx: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  tailwindApi: PropTypes.bool,
};

export default FormGroup;
